/**
 * "Jetzt löschen" — the manual half of the retention window.
 *
 * Deletion is irreversible, so the reader is told what will be destroyed
 * *before* the second press, in plain words and without euphemism: the PDFs,
 * the facts, the runs and their reports. It also says what stays — the audit
 * trail records that a deletion happened, without the deleted values — because
 * "everything is gone" would be a lie and someone will ask.
 *
 * The confirmation is not only a dialog. `api.deleteDossier` sends the dossier
 * id in `confirm=`, and the server refuses with 422 without it; this panel is
 * the human half of a step that exists in the contract either way.
 *
 * Keyboard: the confirmation is an inline `role="alertdialog"` region rather
 * than a modal — no focus trap to get wrong, Escape closes it, and the two
 * buttons are in the normal tab order. The destructive button is the one that
 * has to be reached deliberately, so it is never the first focus target.
 */
import { useEffect, useRef, useState } from 'react'
import { Trash2 } from 'lucide-react'

import { api } from '@/api/client'
import { ErrorNote } from '@/components/ErrorNote'
import { Icon } from '@/components/Icon'
import { Button } from '@/components/ui/button'
import { useI18n } from '@/i18n'
import type { DossierDeleted, DossierRef } from '@/api/types'

export function DeleteDossierButton({
  dossier,
  onDeleted,
}: {
  dossier: DossierRef
  onDeleted?: (result: DossierDeleted) => void
}) {
  const { t } = useI18n()
  const [asking, setAsking] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const panelRef = useRef<HTMLDivElement | null>(null)

  // Opening the question moves focus into it — otherwise a keyboard reader
  // would have to hunt for the panel that just appeared below their button.
  // Focus lands on «Abbrechen», never on the destructive button: the way out
  // is the default, and the deletion has to be reached on purpose.
  useEffect(() => {
    if (asking) panelRef.current?.querySelector('button')?.focus()
  }, [asking])

  const remove = async () => {
    setBusy(true)
    setError(null)
    try {
      const result = await api.deleteDossier(dossier.workspace_id, dossier.id)
      setAsking(false)
      onDeleted?.(result)
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause))
    } finally {
      setBusy(false)
    }
  }

  if (!asking) {
    return (
      <Button
        size="sm"
        variant="ghost"
        onClick={() => setAsking(true)}
        data-testid="delete-dossier"
      >
        <Icon icon={Trash2} />
        {t('delete.cta')}
      </Button>
    )
  }

  return (
    <div
      role="alertdialog"
      aria-label={t('delete.title')}
      aria-describedby={`delete-what-${dossier.id}`}
      onKeyDown={(event) => {
        if (event.key === 'Escape') setAsking(false)
      }}
      ref={panelRef}
      className="flex w-full flex-col gap-3 rounded-sm border border-border-strong bg-surface px-4 py-3"
      data-testid="delete-dossier-confirm"
    >
      <p className="text-[15px] font-medium text-ink">{t('delete.title')}</p>
      <div id={`delete-what-${dossier.id}`} className="flex flex-col gap-1 text-[13px] text-muted">
        <p>{t('delete.what')}</p>
        <p className="font-medium text-signal-red-text">{t('delete.irreversible')}</p>
        <p>{t('delete.keeps')}</p>
      </div>
      {error ? <ErrorNote message={error} /> : null}
      <div className="flex flex-wrap gap-3">
        <Button
          size="sm"
          variant="secondary"
          onClick={() => setAsking(false)}
          disabled={busy}
        >
          {t('delete.cancel')}
        </Button>
        <Button
          size="sm"
          variant="primary"
          onClick={() => void remove()}
          disabled={busy}
          data-testid="delete-dossier-confirmed"
          className="bg-signal-red border-signal-red hover:bg-signal-red-text hover:border-signal-red-text"
        >
          <Icon icon={Trash2} />
          {busy ? t('delete.busy') : t('delete.confirm')}
        </Button>
      </div>
    </div>
  )
}
